<template>
	<div class="grid-flex-container">
		<grid-toolbar
			:gridViewName="gridView.Name"
			:quickFilter="quickFilter"
			:advancedFilter="advancedFilter"
			:visibleColumns="visibleColumns"
			:hiddenColumns="hiddenColumns"
			@quickFilterChanged="onQuickFilterChanged"
			@advancedFilterChanged="onAdvancedFilterChanged"
			@multipleSelectionClick="onMultipleSelectionClick"
			@bulkDelete="bulkDelete"
			@bulkEdit="bulkEdit"
			@mergeEntities="onMergeEntities"
		/>
		<div class="generic-grid-container">
			<grid-view
				v-if="visibleColumns"
				:gridData="gridData"
				:irisUserMetadata="irisUserMetadata"
				:loadingData="gridLoading"
				:visibleColumns="visibleColumns"
				:sorting="sorting"
				:page="page"
				:rowsPerPage="rowsPerPage"
				:totalCount="totalCount"
				:hasActions="true"
				:selectedRows="selectedRows"
				:multipleSelection="multipleSelection"
				@rowsPerPageChanged="onRowsPerPageChanged"
				@pageChanged="onPageChanged"
				@sortChanged="onSortChanged"
				@rowSelectionChanged="onRowSelectionChanged"
			>
				<template v-slot:[`actionsColumns`]="{ item }">
					<td class="fixed-col">
						<v-btn
							v-if="
								gridItemConnectors(item) &&
								gridItemConnectors(item).length > 0
							"
							fab
							small
							icon
							elevation="1"
							class="action-btn"
							@click="openConnectorsModal(item)"
						>
							<v-icon color="warning" small
								>mdi-connection</v-icon
							>
						</v-btn>

						<v-btn
							fab
							small
							icon
							elevation="1"
							class="action-btn"
							@click="openItemHistory(item.Id)"
						>
							<v-icon color="info" small>mdi-history</v-icon>
						</v-btn>
						<v-btn
							v-if="canEdit"
							fab
							small
							icon
							elevation="1"
							class="action-btn"
							@click="editItem(item.Id)"
						>
							<v-icon color="primary" small>mdi-pencil</v-icon>
						</v-btn>
						<v-btn
							v-if="canDelete"
							fab
							small
							icon
							elevation="1"
							class="action-btn"
							@click="deleteItem(item.Id)"
						>
							<v-icon color="error" small>mdi-delete</v-icon>
						</v-btn>
					</td>
				</template>
			</grid-view>
			<grid-view-connectors-modal
				v-if="showConnectorsModal"
				:gridView="gridView"
				:parentItem="connectorParentItem"
				:showDialog="showConnectorsModal"
				@close="onConnectorsModalClose"
			/>
			<bulk-edit-modal
				v-if="showBulkEditModal"
				:showDialog="showBulkEditModal"
				:selectedRows="selectedRows"
				@close="onCloseBulkEditModal"
				@save="onSaveBulkEditModal"
			/>

			<merge-entities-modal
				v-if="showMergeEntitiesModal"
				:showDialog="showMergeEntitiesModal"
				:gridView="gridView"
				:visibleColumns="visibleColumns"
				:irisUserMetadata="irisUserMetadata"
				:items="selectedRowsData"
				@close="onCloseMergeEntitiesModal"
				@refresh="onMergeSuccess"
			/>
		</div>
	</div>
</template>
<script>
import GridToolbar from './GridToolbar.vue';
import GridView from '../../shared/gridViews/GridView.vue';
import ConfirmDialog from '@/components/core/dialogs/confirmDialog';
import BulkEditModal from '../edit/BulkEditModal.vue';
import permissionsMixin from '../../../mixins/permissionsMixin';
import gridViewConnctorsMixin from '../../../mixins/gridViewConnectors/gridViewConnectorsMixin';
import { Permissions } from '../../../enums/permissionsEnum';
import { mapGetters } from 'vuex';
import { GridsPageGetters } from '../../../store/getters';
import { GridsPageActions } from '../../../store/actions';
import GridViewConnectorsModal from '../../shared/gridViewConnectors/GridViewConnectorsModal.vue';
import MergeEntitiesModal from '../merge/MergeEntitiesModal.vue';
import Snackbar from '../../core/dialogs/snackbarMessage';

export default {
	name: 'GridTable',
	components: {
		GridToolbar,
		GridView,
		GridViewConnectorsModal,
		BulkEditModal,
		MergeEntitiesModal,
	},
	mixins: [permissionsMixin, gridViewConnctorsMixin],
	data() {
		return {
			connectorParentItem: null,
			showConnectorsModal: false,
			multipleSelection: false,
			showBulkEditModal: false,
			showMergeEntitiesModal: false,
			selectedRows: [],
			selectedRowsData: [],
		};
	},
	computed: {
		...mapGetters({
			//for toolbar only
			quickFilter: [GridsPageGetters.QUICK_FILTER],
			gridView: [GridsPageGetters.GRID_VIEW],
			advancedFilter: [GridsPageGetters.ADVANCED_FILTER],
			hiddenColumns: [GridsPageGetters.HIDDEN_COLUMNS],
			//for toolbar & grid
			visibleColumns: [GridsPageGetters.VISIBLE_COLUMNS],
			//for grid only
			gridData: [GridsPageGetters.GRID_DATA],
			irisUserMetadata: [GridsPageGetters.IRIS_USER_METADATA],
			gridLoading: [GridsPageGetters.GRID_LOADING],
			sorting: [GridsPageGetters.SORTING],
			page: [GridsPageGetters.PAGE],
			totalCount: [GridsPageGetters.TOTAL_COUNT],
			rowsPerPage: [GridsPageGetters.ROWS_PER_PAGE],
		}),
		entityType() {
			return this.$route.params.pageName;
		},

		canAdd() {
			return this.hasPermissions([Permissions.Grids.Add]);
		},
		canEdit() {
			return this.hasPermissions([Permissions.Grids.Edit]);
		},
		canDelete() {
			return this.hasPermissions([Permissions.Grids.Delete]);
		},
	},
	methods: {
		onRowSelectionChanged(rows, selectAll) {
			if (!rows) return;
			rows.forEach((row) => {
				let index = this.selectedRows.indexOf(row);
				let dataIndex = this.selectedRowsData.findIndex(
					(item) => item.Id === row
				);
				let fullRowData = this.gridData.find((item) => item.Id === row);

				if (selectAll === null) {
					//single row click
					if (index >= 0) {
						this.selectedRows.splice(index, 1);
						if (dataIndex >= 0) {
							this.selectedRowsData.splice(dataIndex, 1);
						}
					} else {
						this.selectedRows.push(row);
						if (fullRowData && dataIndex < 0) {
							this.selectedRowsData.push(fullRowData);
						}
					}
				} else {
					//select all
					if (selectAll) {
						if (index < 0) {
							this.selectedRows.push(row);
							if (fullRowData && dataIndex < 0) {
								this.selectedRowsData.push(fullRowData);
							}
						}
					} else {
						if (index >= 0) {
							this.selectedRows.splice(index, 1);
							if (dataIndex >= 0) {
								this.selectedRowsData.splice(dataIndex, 1);
							}
						}
					}
				}
			});
		},
		openConnectorsModal(item) {
			this.connectorParentItem = item;
			this.showConnectorsModal = true;
		},
		onConnectorsModalClose() {
			this.connectorParentItem = null;
			this.showConnectorsModal = false;
		},

		onPageChanged(value) {
			this.$store.dispatch(GridsPageActions.UPDATE_PAGE, value);
		},
		onRowsPerPageChanged(value) {
			this.$store.dispatch(GridsPageActions.UPDATE_ROWS_PER_PAGE, value);
		},
		onSortChanged(value) {
			this.$store.dispatch(GridsPageActions.UPDATE_SORTING, value);
		},
		onQuickFilterChanged(value) {
			this.$store.dispatch(GridsPageActions.UPDATE_QUICK_FILTER, value);
		},
		onAdvancedFilterChanged(value) {
			this.$store.dispatch(
				GridsPageActions.UPDATE_ADVANCED_FILTER,
				value
			);
		},
		editItem(Id) {
			this.$router.push(`${this.gridView.Entity.Name}/edit/${Id}`);
		},
		openItemHistory(Id) {
			this.$router.push(`${this.gridView.Entity.Name}/history/${Id}`);
		},
		onMultipleSelectionClick(value) {
			this.multipleSelection = value;
		},
		deleteItem(Id) {
			ConfirmDialog({
				message:
					'This record may have dependent records in other tables. Please be sure any dependencies have been cleared before deletion. Continue with this deletion?',
			}).then((isDelete) => {
				if (isDelete) {
					this.$store.dispatch(GridsPageActions.DELETE_ASSET, {
						entityType: this.entityType,
						id: Id,
						entityId: this.gridView.EntityId,
					});
				}
			});
		},
		bulkDelete() {
			ConfirmDialog({
				message: `WARNING! You are about to delete ${this.selectedRows.length} records. Those may have dependent records in other tables. Please be sure any dependencies have been cleared before deletion. Continue with this deletion?`,
				modalWidth: '600',
			}).then((isDelete) => {
				if (isDelete) {
					this.$store
						.dispatch(GridsPageActions.DELETE_BULK_ASSETS, {
							entityType: this.entityType,
							items: this.selectedRows,
							entityId: this.gridView.EntityId,
						})
						.then((succes) => {
							if (succes) {
								this.selectedRows = [];
								this.selectedRowsData = [];
								this.$store.dispatch(
									GridsPageActions.LOAD_GRID_DATA
								);
							}
						});
				}
			});
		},
		bulkEdit() {
			this.showBulkEditModal = true;
		},
		onCloseBulkEditModal() {
			this.showBulkEditModal = false;
		},
		onSaveBulkEditModal() {
			this.showBulkEditModal = false;
			this.selectedRows = [];
			this.selectedRowsData = [];
			this.$store.dispatch(GridsPageActions.LOAD_GRID_DATA);
		},
		onMergeEntities() {
			// Check if any items are selected
			if (!this.selectedRows || this.selectedRows.length === 0) {
				Snackbar({
					message: 'Please select records to merge',
				});
				return;
			}
			this.showMergeEntitiesModal = true;
		},
		onCloseMergeEntitiesModal() {
			this.showMergeEntitiesModal = false;
		},
		onMergeSuccess() {
			// Close the modal and refresh the grid
			this.showMergeEntitiesModal = false;
			this.selectedRows = [];
			this.selectedRowsData = [];
			this.$store.dispatch(GridsPageActions.LOAD_GRID_DATA);
		},
	},
};
</script>
